import { CallState, LoadingState } from '@lcs/shared/api';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { ExtensionTypes } from 'generated/graphql-types';
import { onReset } from 'src/app/authentication/reset-reducer';
import {
   extensionCallQueueIsLoggedInChanged,
   extensionCallQueueStatusChanged,
} from 'src/app/signalr/extension-call-queue-hub/extension-call-queue-hub.actions';
import {
   clearMailboxConfirmed,
   saveMailboxMessageDetailSuccess,
} from '../../mailbox-messages/state/mailbox-message-detail.actions';
import { ExtensionHackedFraudDetails } from '../helpers/hacked-fraud-helper';
import {
   ExtensionDetailView,
   ExtensionNumberDetail,
   ExternalNumberDetail,
} from '../models/extension-detail-view.model';
import {
   GetExtensionDetail_extension,
   GetExtensionDetail_extension_mailbox_mailboxMessagesConnection_items,
} from '../queries/generated/GetExtensionDetail';
import { SaveExtension_extension } from '../queries/generated/SaveExtension';
import * as ExtensionDetailActions from './extension-detail.actions';

export const extensionDetailFeatureKey = 'extensionDetail';

export interface ExtensionDetailState extends EntityState<ExtensionDetailView> {
   callState: CallState;
   extension: ExtensionDetailView | null;
   selectedExtensionId: number | null;
   hackedFraudDetails: ExtensionHackedFraudDetails | null;
   accountExtensions: ExtensionNumberDetail[];
   externalNumbers: ExternalNumberDetail[];
}

export const extensionAdapter = createEntityAdapter<ExtensionDetailView>({
   selectId: (extension) => extension.extensionId,
});

export const initialState: ExtensionDetailState = extensionAdapter.getInitialState({
   callState: LoadingState.Init,
   selectedExtensionId: null,
   extension: null,
   hackedFraudDetails: null,
   accountExtensions: [],
   externalNumbers: [],
});

export const reducer = createReducer(
   initialState,
   onReset(initialState),
   on(ExtensionDetailActions.extensionDetailLoad, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         extension: null,
         hackedFraudDetails: null,
      };
   }),
   on(ExtensionDetailActions.saveExtensionDetail, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(ExtensionDetailActions.saveExtensionDetailFailure, ExtensionDetailActions.loadExtensionAccessDenied, (state) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
      };
   }),
   on(ExtensionDetailActions.getHackedFraudDetailsSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         hackedFraudDetails: action.details,
      };
   }),
   on(ExtensionDetailActions.changeSipUserPasswordSuccess, (state) => {
      return {
         ...state,
         hackedFraudDetails: null,
      };
   }),

   on(extensionCallQueueIsLoggedInChanged, (state, action) => {
      const extension = state.entities[action.queueExtensionId];

      if (!extension || !extension.extensionQueue || !extension.extensionQueue.queueMembersConnection?.items) {
         return state;
      }

      const updatedQueueMembers = extension.extensionQueue.queueMembersConnection.items.map((member) => {
         if (member.queueMemberId === action.queueMemeberId) {
            return {
               ...member,
               isLoggedIn: action.isLoggedIn,
               agentStatus: action.agentStatus,
            };
         }
         return member;
      });

      return extensionAdapter.updateOne(
         {
            id: action.queueExtensionId,
            changes: {
               extensionQueue: {
                  ...extension.extensionQueue,
                  queueMembersConnection: {
                     ...extension.extensionQueue.queueMembersConnection,
                     items: updatedQueueMembers,
                  },
               },
            },
         },
         state
      );
   }),
   on(extensionCallQueueStatusChanged, (state, action) => {
      let updatedState = state;

      for (const queueExtensionId of action.extensionIds) {
         const extension = updatedState.entities[queueExtensionId];

         if (!extension || !extension.extensionQueue || !extension.extensionQueue.queueMembersConnection?.items) {
            continue;
         }

         const updatedQueueMembers = extension.extensionQueue.queueMembersConnection.items.map((member) => {
            if (member.agentExtension.extensionId === action.agentExtensionId) {
               return {
                  ...member,
                  agentStatus: action.agentStatus,
                  endWrapUpTime: action.endWrapUpTime,
               };
            }
            return member;
         });

         updatedState = extensionAdapter.updateOne(
            {
               id: queueExtensionId,
               changes: {
                  extensionQueue: {
                     ...extension.extensionQueue,
                     queueMembersConnection: {
                        ...extension.extensionQueue.queueMembersConnection,
                        items: updatedQueueMembers,
                     },
                  },
               },
            },
            updatedState
         );
      }

      return updatedState;
   }),

   // on(ExtensionDetailActions.changeDNDStatusSuccess, (state, action) => {
   //    if (!state.extension || !state.extension.extensionPhone) {
   //       return state;
   //    }

   //    const updatedExtension: ExtensionDetailView = {
   //       ...state.extension,
   //       extensionPhone: {
   //          ...state.extension.extensionPhone,
   //          dndStatus: action.dndStatus,
   //       },
   //    };

   //    return {
   //       ...state,
   //       extension: updatedExtension,
   //    };
   // }),
   on(ExtensionDetailActions.loadExtensionDetailSuccess, (state, action) => {
      const extension: GetExtensionDetail_extension = action.data;
      const typeId = Number(extension.type);

      const transformedExtension: ExtensionDetailView = {
         ...extension,
         accountId: extension.account?.accountId,
         adminNote: extension.adminNote,
         extensionNote: extension.extensionNote,
         accountIsOtrEnabled: extension?.account.isOtrEnabled,
         accountIsCallRecordingFeatureEnabled: extension?.account.isCallRecordingFeatureEnabled,
         type: {
            displayName: extension.extensionType?.displayName ?? '',
            extensionTypeId: typeId,
            name: ExtensionTypes[extension.type],
            extensions: [] as any,
            extensionGroups: [] as any,
         },
         autoAttendantsUsingForTimeout: extension.autoAttendantsUsingForTimeoutConnection.items.map((item) => ({
            extensionId: item.extension.extensionId,
            name: item.extension.name,
            number: item.extension.number,
         })),
         fmFmsUsingForFinalDestination: extension.fmFmsUsingForFinalDestinationConnection.items.map((item) => ({
            extensionId: item.extension.extensionId,
            name: item.extension.name,
            number: item.extension.number,
         })),
         queuesUsingForExitRoute: extension.queuesUsingForExitRouteConnection.items.map((item) => ({
            extensionId: item.extension.extensionId,
            name: item.extension.name,
            number: item.extension.number,
         })),
         huntGroupsUsingForRouteBusy: extension.huntGroupsUsingForRouteBusyConnection.items.map((item) => ({
            extensionId: item.extension.extensionId,
            name: item.extension.name,
            number: item.extension.number,
         })),
         huntGroupsUsingForRouteNoAnswer: extension.huntGroupsUsingForRouteNoAnswerConnection.items.map((item) => ({
            extensionId: item.extension.extensionId,
            name: item.extension.name,
            number: item.extension.number,
         })),
      };

      return extensionAdapter.upsertOne(transformedExtension, {
         ...state,
         callState: LoadingState.Loaded,
         hackedFraudDetails: null,
         selectedExtensionId: transformedExtension.extensionId,
         extension: transformedExtension,
      });
   }),
   on(ExtensionDetailActions.loadExtensionDetailFailure, (state, action) => {
      return {
         ...state,
         callState: action,
      };
   }),
   on(ExtensionDetailActions.loadExternalNumbersSuccess, (state, action) => {
      return {
         ...state,
         externalNumbers: action.data.externalNumberHistoriesConnection.items || null,
      };
   }),
   on(saveMailboxMessageDetailSuccess, (state, action) => {
      if (!state.extension?.mailbox?.mailboxMessagesConnection.items) return state;
      const items: GetExtensionDetail_extension_mailbox_mailboxMessagesConnection_items[] = [];
      const updatedMessages = items.map((item) =>
         item.mailboxMessageId === action.data.mailboxMessage.mailboxMessageId
            ? { ...item, isRead: action.data.mailboxMessage.isRead }
            : item
      );

      return {
         ...state,
         extension: {
            ...state.extension,
            mailbox: {
               ...state.extension?.mailbox,
               mailboxMessagesConnection: { items: updatedMessages },
            },
         },
      };
   }),
   on(ExtensionDetailActions.saveExtensionDetailSuccess, (state, action) => {
      const saveExtension: SaveExtension_extension = action.data.extension;
      const existingExtension: ExtensionDetailView = <ExtensionDetailView>state.extension;
      const typeId = Number(saveExtension.type);
      return {
         ...state,
         extension: {
            ...saveExtension,
            createDate: existingExtension.createDate,
            createUser: existingExtension.createUser,
            extensionAutoAttendant: existingExtension.extensionAutoAttendant,
            extensionExternalForward: existingExtension.extensionExternalForward,
            extensionHuntGroup: existingExtension.extensionHuntGroup,
            extensionPagingGroup: existingExtension.extensionPagingGroup,
            extensionMailbox: existingExtension.extensionMailbox,
            extensionPhone: existingExtension.extensionPhone,
            extensionQueue: existingExtension.extensionQueue,
            extensionFax: existingExtension.extensionFax,
            extensionFmFm: existingExtension.extensionFmFm,
            location: existingExtension.location,
            adminNote: existingExtension.adminNote,
            extensionNote: existingExtension.extensionNote,
            mailbox: existingExtension.mailbox,
            mailboxId: existingExtension.mailboxId,
            timeBasedRoutesConnection: existingExtension.timeBasedRoutesConnection,
            type: {
               displayName: existingExtension.type?.displayName ?? '',
               extensionTypeId: typeId,
               name: ExtensionTypes[saveExtension.type],
               extensions: [] as any,
               extensionGroups: [] as any,
            },
            updateDate: existingExtension.updateDate,
            updateUser: existingExtension.updateUser,
            usersConnection: existingExtension.usersConnection,
         },
         hackedFraudDetails: null,
      };
   }),
   on(ExtensionDetailActions.loadExtensionMailbox, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(ExtensionDetailActions.loadExtensionMailboxSuccess, (state, action) => {
      const selectedExtensionId = state.selectedExtensionId;

      if (selectedExtensionId == null) {
         return {
            ...state,
            callState: LoadingState.Loaded,
         };
      }

      const stateWithUpdatedEntity = extensionAdapter.updateOne(
         {
            id: selectedExtensionId,
            changes: { mailbox: action.mailbox },
         },
         state
      );

      return {
         ...stateWithUpdatedEntity,
         callState: LoadingState.Loaded,
         extension: stateWithUpdatedEntity.entities[selectedExtensionId] ?? null,
      };
   }),

   on(ExtensionDetailActions.loadExtensionMailboxFailure, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
      };
   }),
   on(ExtensionDetailActions.extensionDetailCancel, (state) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
      };
   }),
   on(ExtensionDetailActions.loadAccountExtensionsSuccess, (state, action) => {
      return {
         ...state,
         accountExtensions: action.data,
      };
   }),
   on(ExtensionDetailActions.resetMwiConfirmation, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(clearMailboxConfirmed, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(
      ExtensionDetailActions.resetMwiSuccess,
      ExtensionDetailActions.resetMwiFailure,
      ExtensionDetailActions.resetMwiCancel,
      (state) => {
         return {
            ...state,
            callState: LoadingState.Loaded,
         };
      }
   )
);
